'use client'

/**
 * TimezoneSelector — compact select for client timezone override.
 * Renders a visible <select> with common IANA TZs plus an "Other" sentinel
 * that reveals a free-form text input.
 */

import { useState } from 'react'
import styles from './TimezoneSelector.module.css'

const COMMON_TIMEZONES = [
  { value: 'America/Santiago', label: 'Chile (Santiago)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'America/Sao_Paulo', label: 'Brazil (São Paulo)' },
  { value: 'America/New_York', label: 'US East (New York)' },
  { value: 'America/Los_Angeles', label: 'US West (Los Angeles)' },
  { value: 'Europe/Madrid', label: 'Spain (Madrid)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'Australia/Sydney', label: 'Australia (Sydney)' },
] as const

const OTHER_SENTINEL = '__other__'

interface TimezoneSelectorProps {
  value: string
  onChange: (tz: string) => void
}

export default function TimezoneSelector({ value, onChange }: TimezoneSelectorProps) {
  const knownValues = COMMON_TIMEZONES.map((t) => t.value as string)
  const isKnown = knownValues.includes(value)

  const [showOther, setShowOther] = useState(!isKnown)
  const [otherValue, setOtherValue] = useState(isKnown ? '' : value)

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    if (v === OTHER_SENTINEL) {
      setShowOther(true)
      // keep current tz until user types
    } else {
      setShowOther(false)
      onChange(v)
    }
  }

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim()
    setOtherValue(v)
    if (v) onChange(v)
  }

  const selectValue = showOther ? OTHER_SENTINEL : (isKnown ? value : OTHER_SENTINEL)

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor="tz-select">
        Your timezone:
      </label>
      <div className={styles.row}>
        <select
          id="tz-select"
          className={styles.select}
          value={selectValue}
          onChange={handleSelectChange}
        >
          {COMMON_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
          <option value={OTHER_SENTINEL}>Other…</option>
        </select>

        {showOther && (
          <input
            type="text"
            className={styles.otherInput}
            placeholder="e.g. Asia/Tokyo"
            value={otherValue}
            onChange={handleOtherChange}
            aria-label="Custom timezone"
          />
        )}
      </div>
    </div>
  )
}
